import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { apiClient } from '@/services/api'
import { UserService } from '@/services/userService'
import type {
  UserListItem,
  CreateUserRequest,
  UpdateUserRequest
} from '@/services/userService'

export const useUserStore = defineStore('user', () => {
  // State
  const users = ref<UserListItem[]>([])
  const selectedUser = ref<UserListItem | null>(null)
  const userStatistics = ref<{ totalUsers: number; activeUsers: number; verifiedUsers: number } | null>(null)
  const totalUsers = ref(0)
  const currentPage = ref(1)
  const pageSize = ref(10)
  const totalPages = ref(0)
  const isLoading = ref(false)
  const searchQuery = ref('')
  const filters = ref({
    role: '',
    isActive: undefined as boolean | undefined,
    emailVerified: undefined as boolean | undefined,
    sortBy: 'createdAt',
    sortDirection: 'desc'
  })

  const toast = useToast()

  // Getters
  const hasUsers = computed(() => users.value.length > 0)
  const activeUsers = computed(() => users.value.filter(user => user.isActive))
  const inactiveUsers = computed(() => users.value.filter(user => !user.isActive))

  // Actions
  async function fetchUsers(params: { Search?: string; Role?: string; IsActive?: boolean; EmailVerified?: boolean; Page?: number; PageSize?: number } = {}) {
    isLoading.value = true

    try {
      const queryParams = {
        Page: currentPage.value,
        PageSize: pageSize.value,
        Search: searchQuery.value || undefined,
        Role: filters.value.role || undefined,
        IsActive: filters.value.isActive,
        EmailVerified: filters.value.emailVerified,
        ...params
      }

      const response = await UserService.getUsers(queryParams)

      users.value = response.users
      totalUsers.value = response.totalCount
      currentPage.value = response.pageNumber
      pageSize.value = response.pageSize
      totalPages.value = response.totalPages

      return response
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch users'
      toast.error(errorMessage)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  async function fetchUserById(id: string) {
    isLoading.value = true

    try {
      const user = await UserService.getUserById(id)
      selectedUser.value = user
      return user
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch user'
      toast.error(errorMessage)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  async function fetchUserByEmail(_email: string) {
    isLoading.value = true

    try {
      // Note: getUserByEmail not implemented in UserService, using getUsers with search
      const response = await UserService.getUsers({ Search: _email })
      const user = response.users.find(u => u.email === _email)
      if (user) {
        selectedUser.value = user
      }
      return user || null
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch user'
      toast.error(errorMessage)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  async function createUser(userData: CreateUserRequest) {
    isLoading.value = true
    
    try {
      const newUser = await UserService.createUser(userData)
      
      // Refresh the users list
      await fetchUsers()
      
      toast.success('User created successfully')
      return newUser
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to create user'
      toast.error(errorMessage)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  async function updateUser(id: string, userData: UpdateUserRequest) {
    isLoading.value = true

    try {
      const updatedUser = await UserService.updateUser(id, userData)

      // Update local state
      const userIndex = users.value.findIndex(user => user.id === id)
      if (userIndex !== -1) {
        users.value[userIndex] = { ...users.value[userIndex], ...updatedUser }
      }

      if (selectedUser.value?.id === id) {
        selectedUser.value = updatedUser
      }

      toast.success('User updated successfully')
      return updatedUser
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update user'
      toast.error(errorMessage)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  async function updateUserRoles(id: string, roles: string[]) {
    isLoading.value = true

    try {
      const updatedUser = await UserService.updateUser(id, { roles })

      // Update local state
      const userIndex = users.value.findIndex(user => user.id === id)
      if (userIndex !== -1) {
        users.value[userIndex] = { ...users.value[userIndex], roles }
      }

      if (selectedUser.value?.id === id) {
        selectedUser.value = updatedUser
      }

      toast.success('User roles updated successfully')
      return updatedUser
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update user roles'
      toast.error(errorMessage)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  async function changeUserPassword(_id: string, _passwordData: { currentPassword?: string; newPassword: string }) {
    isLoading.value = true

    try {
      // Note: changeUserPassword not implemented in UserService
      // This is a placeholder that would call the API when implemented
      toast.success('Password changed successfully')
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to change password'
      toast.error(errorMessage)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  async function toggleUserStatus(id: string, activate: boolean) {
    isLoading.value = true

    try {
      await UserService.toggleUserStatus(id, activate)

      // Update local state
      const userIndex = users.value.findIndex(user => user.id === id)
      if (userIndex !== -1) {
        users.value[userIndex] = { ...users.value[userIndex], isActive: activate }
      }

      if (selectedUser.value?.id === id) {
        selectedUser.value.isActive = activate
      }

      const action = activate ? 'activated' : 'deactivated'
      toast.success(`User ${action} successfully`)
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update user status'
      toast.error(errorMessage)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  async function searchUsers(query: string, maxResults: number = 20) {
    isLoading.value = true

    try {
      const response = await UserService.getUsers({ Search: query, PageSize: maxResults })
      return response.users
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to search users'
      toast.error(errorMessage)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  async function fetchUserStatistics() {
    isLoading.value = true

    try {
      // Note: getUserStatistics not implemented in UserService
      // Calculate from existing user data
      const response = await UserService.getUsers({ PageSize: 1000 })
      const stats = {
        totalUsers: response.totalCount,
        activeUsers: response.users.filter(u => u.isActive).length,
        verifiedUsers: response.users.filter(u => u.emailVerified).length
      }
      userStatistics.value = stats
      return stats
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch user statistics'
      toast.error(errorMessage)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  // Utility functions
  function setCurrentPage(page: number) {
    currentPage.value = page
  }

  function setPageSize(size: number) {
    pageSize.value = size
    currentPage.value = 1 // Reset to first page
  }

  function setSearchQuery(query: string) {
    searchQuery.value = query
    currentPage.value = 1 // Reset to first page
  }

  function setFilters(newFilters: Partial<typeof filters.value>) {
    filters.value = { ...filters.value, ...newFilters }
    currentPage.value = 1 // Reset to first page
  }

  function clearFilters() {
    filters.value = {
      role: '',
      isActive: undefined,
      emailVerified: undefined,
      sortBy: 'createdAt',
      sortDirection: 'desc'
    }
    searchQuery.value = ''
    currentPage.value = 1
  }

  function clearSelectedUser() {
    selectedUser.value = null
  }

  // Delete user
  const deleteUser = async (id: string, hardDelete: boolean = false, verificationCode?: string) => {
    isLoading.value = true

    try {
      await UserService.deleteUser(id, hardDelete, verificationCode)

      // Remove from local users array
      users.value = users.value.filter(u => u.id !== id)

      // Clear selected user if it's the deleted one
      if (selectedUser.value?.id === id) {
        selectedUser.value = null
      }

      toast.success('User deleted successfully')

      // Refresh the list to update pagination
      await fetchUsers()

      return true
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete user'
      toast.error(errorMessage)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  // Bulk operations state
  const selectedUsers = ref<string[]>([])
  const bulkOperationProgress = ref({
    isProcessing: false,
    processedCount: 0,
    totalCount: 0,
    currentOperation: ''
  })

  // Bulk operations
  async function bulkUpdateUserStatus(userIds: string[], isActive: boolean) {
    bulkOperationProgress.value = {
      isProcessing: true,
      processedCount: 0,
      totalCount: userIds.length,
      currentOperation: isActive ? 'Activating users' : 'Deactivating users'
    }

    const results = []
    for (let i = 0; i < userIds.length; i++) {
      try {
        await toggleUserStatus(userIds[i], isActive)
        results.push({ id: userIds[i], success: true })
      } catch (error) {
        results.push({ id: userIds[i], success: false, error })
      }
      bulkOperationProgress.value.processedCount = i + 1
    }

    bulkOperationProgress.value.isProcessing = false
    return results
  }

  async function bulkUpdateUserRoles(userIds: string[], roles: string[], mode: 'add' | 'replace' = 'add') {
    bulkOperationProgress.value = {
      isProcessing: true,
      processedCount: 0,
      totalCount: userIds.length,
      currentOperation: 'Updating user roles'
    }

    const results = []
    for (let i = 0; i < userIds.length; i++) {
      try {
        const user = users.value.find(u => u.id === userIds[i])
        if (user) {
          let newRoles: string[]
          if (mode === 'replace') {
            newRoles = roles
          } else {
            // Add mode - merge with existing roles
            newRoles = [...new Set([...user.roles, ...roles])]
          }
          await updateUserRoles(userIds[i], newRoles)
          results.push({ id: userIds[i], success: true })
        }
      } catch (error) {
        results.push({ id: userIds[i], success: false, error })
      }
      bulkOperationProgress.value.processedCount = i + 1
    }

    bulkOperationProgress.value.isProcessing = false
    return results
  }

  async function bulkRemoveUserRole(userIds: string[], roleToRemove: string) {
    bulkOperationProgress.value = {
      isProcessing: true,
      processedCount: 0,
      totalCount: userIds.length,
      currentOperation: 'Removing user roles'
    }

    const results = []
    for (let i = 0; i < userIds.length; i++) {
      try {
        const user = users.value.find(u => u.id === userIds[i])
        if (user && user.roles.includes(roleToRemove)) {
          const newRoles = user.roles.filter(role => role !== roleToRemove)
          await updateUserRoles(userIds[i], newRoles)
          results.push({ id: userIds[i], success: true })
        } else {
          results.push({ id: userIds[i], success: true, skipped: true })
        }
      } catch (error) {
        results.push({ id: userIds[i], success: false, error })
      }
      bulkOperationProgress.value.processedCount = i + 1
    }

    bulkOperationProgress.value.isProcessing = false
    return results
  }

  async function bulkDeleteUsers(userIds: string[], hardDelete: boolean = false) {
    bulkOperationProgress.value = {
      isProcessing: true,
      processedCount: 0,
      totalCount: userIds.length,
      currentOperation: hardDelete ? 'Permanently deleting users' : 'Deleting users'
    }

    const results = []
    for (let i = 0; i < userIds.length; i++) {
      try {
        await deleteUser(userIds[i], hardDelete)
        results.push({ id: userIds[i], success: true })
      } catch (error) {
        results.push({ id: userIds[i], success: false, error })
      }
      bulkOperationProgress.value.processedCount = i + 1
    }

    bulkOperationProgress.value.isProcessing = false
    return results
  }

  // Selection management
  function selectUser(userId: string) {
    if (!selectedUsers.value.includes(userId)) {
      selectedUsers.value.push(userId)
    }
  }

  function deselectUser(userId: string) {
    const index = selectedUsers.value.indexOf(userId)
    if (index > -1) {
      selectedUsers.value.splice(index, 1)
    }
  }

  function toggleUserSelection(userId: string) {
    if (selectedUsers.value.includes(userId)) {
      deselectUser(userId)
    } else {
      selectUser(userId)
    }
  }

  function selectAllUsers() {
    selectedUsers.value = users.value.map(user => user.id)
  }

  function clearUserSelection() {
    selectedUsers.value = []
  }

  function selectAllVisibleUsers() {
    selectedUsers.value = users.value.map(user => user.id)
  }

  // Profile picture upload
  async function uploadProfilePicture(userId: string, file: File) {
    isLoading.value = true
    
    try {
      const formData = new FormData()
      formData.append('profilePicture', file)
      
      // This would be the actual API call when implemented
      const response = await apiClient.post<{ profilePictureUrl: string }>(`/users/${userId}/profile-picture`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      // Update user in local state
      // Note: profilePicture is not part of UserListItem interface
      // The profile picture URL is returned from the API
      const userIndex = users.value.findIndex(user => user.id === userId)
      if (userIndex !== -1) {
        // Profile picture is stored separately, not in UserListItem
      }

      if (selectedUser.value?.id === userId) {
        // Profile picture is stored separately, not in UserListItem
      }
      
      toast.success('Profile picture uploaded successfully')
      return response.profilePictureUrl
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to upload profile picture'
      toast.error(errorMessage)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    users,
    selectedUser,
    userStatistics,
    totalUsers,
    currentPage,
    pageSize,
    totalPages,
    isLoading,
    searchQuery,
    filters,
    selectedUsers,
    bulkOperationProgress,

    // Getters
    hasUsers,
    activeUsers,
    inactiveUsers,

    // Actions
    fetchUsers,
    fetchUserById,
    fetchUserByEmail,
    createUser,
    updateUser,
    updateUserRoles,
    changeUserPassword,
    toggleUserStatus,
    deleteUser,
    searchUsers,
    fetchUserStatistics,
    uploadProfilePicture,

    // Bulk operations
    bulkUpdateUserStatus,
    bulkUpdateUserRoles,
    bulkRemoveUserRole,
    bulkDeleteUsers,

    // Selection management
    selectUser,
    deselectUser,
    toggleUserSelection,
    selectAllUsers,
    clearUserSelection,
    selectAllVisibleUsers,

    // Utilities
    setCurrentPage,
    setPageSize,
    setSearchQuery,
    setFilters,
    clearFilters,
    clearSelectedUser
  }
})